import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner } from 'react-bootstrap'
import { brandAPI } from '../api/brandAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function BrandsListScreen() {
    const { hasPermission } = usePermissions()
    const [brands, setBrands] = useState([])
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('brandColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            name: 'auto',
            actions: 100
        };
    });

    useEffect(() => {
        localStorage.setItem('brandColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchBrands() {
            try {
                setLoading(true)
                const response = await brandAPI.getAll(currentPage, pageSize, searchTerm, showDeactivated)
                setBrands(response.results)
                setTotalPages(response.count)
            } finally {
                setLoading(false)
            }
        }
        fetchBrands()
    }, [currentPage, pageSize, searchTerm, showDeactivated])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handleToggleActive = async (e, brand) => {
        e.stopPropagation();

        if (brand.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this brand?')) {
                return;
            }
        }

        try {
            const updatedBrand = {
                ...brand,
                is_active: !brand.is_active
            };

            await brandAPI.update(brand._id, updatedBrand);

            setBrands(brands.map(b =>
                b._id === brand._id
                    ? updatedBrand
                    : b
            ));
        } catch (error) {
            console.error('Error toggling brand status:', error);
            alert(`Failed to update brand: ${error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Brands</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search brands..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDeactivated(!showDeactivated)}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    {hasPermission('stock.items.modify') && (
                        <Button
                            onClick={() => navigate('/brand/create')}
                            variant="success"
                        >
                            Create Brand
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="brands_table"
                        columns={[
                            {
                                id: 'name',
                                header: 'Name',
                                cell: (row) => (
                                    <>
                                        {highlightText(row.name, searchTerm)}
                                        {!row.is_active && (
                                            <span className="ms-2 text-muted fst-italic">
                                                (Inactive)
                                            </span>
                                        )}
                                    </>
                                )
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    hasPermission('stock.items.modify') && (
                                        <Button
                                            variant={row.is_active ? "danger" : "success"}
                                            size="sm"
                                            onClick={(e) => handleToggleActive(e, row)}
                                        >
                                            {row.is_active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                    )
                                )
                            }
                        ]}
                        data={brands}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/brand/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default BrandsListScreen
